/* 
  ProfilePage: Allows users to update their personal info,
  change their avatar, and switch their password.
*/
import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { Input } from '../components/UI/Input';
import { Button } from '../components/UI/Button';
import { ImageUpload } from '../components/UI/ImageUpload';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

export const ProfilePage = () => {
  const { state, updateEntity } = useStore();
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: state.currentUser?.name || '',
    avatar: state.currentUser?.avatar || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSuccess(false);

    try {
      const updateData = {
        ...state.currentUser,
        name: formData.name,
        avatar: formData.avatar
      };

      // Handle password change if requested
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        updateData.password = formData.newPassword;
      }

      await updateEntity('users', updateData);
      setSuccess(true);
      setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900 custom-scrollbar">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Account Settings</h1>
          <p className="text-sm text-gray-400">Manage your profile and security preferences.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Identity Section */}
          <div className="md:col-span-1 space-y-6">
            <div className="bg-base-800 rounded-2xl border border-base-700 p-6 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full border-4 border-base-700 overflow-hidden mb-4 shadow-xl">
                <img 
                  src={formData.avatar || `https://ui-avatars.com/api/?name=${formData.name}&background=6C63FF&color=fff`} 
                  className="w-full h-full object-cover"
                  alt="Avatar"
                />
              </div>
              <h3 className="font-bold text-lg">{state.currentUser?.name}</h3>
              <p className="text-xs text-primary uppercase font-bold tracking-widest mt-1">{state.currentUser?.role}</p>
              <p className="text-xs text-gray-500 mt-2">{state.currentUser?.email}</p>
            </div>
            
            <div className="bg-base-800 rounded-2xl border border-base-700 p-6">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em] mb-4">Storage Usage</h4>
              <div className="h-1.5 w-full bg-base-900 rounded-full overflow-hidden">
                <div className="h-full bg-primary w-[15%]" />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                <span>1.2MB of 10MB used</span>
                <span className="font-bold text-gray-400">12%</span>
              </div>
            </div>
          </div>

          {/* Edit Form */}
          <div className="md:col-span-2 space-y-6">
            <form onSubmit={handleUpdateProfile} className="bg-base-800 rounded-2xl border border-base-700 overflow-hidden shadow-xl">
              <div className="p-6 border-b border-base-700 bg-base-900/30">
                <h3 className="font-bold flex items-center gap-2">
                  <SafeIcon name="User" className="text-primary w-4 h-4" /> Personal Information
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="Display Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                  <Input label="Email Address" value={state.currentUser?.email} disabled className="opacity-50" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-base-700/50">
                  <ImageUpload label="Update Avatar" currentUrl={formData.avatar} onUpload={(url) => setFormData({ ...formData, avatar: url })} />
                  <Input label="Avatar URL" value={formData.avatar} onChange={e => setFormData({ ...formData, avatar: e.target.value })} placeholder="https://..." />
                </div>

                <div className="pt-6 border-t border-base-700">
                  <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
                    <SafeIcon name="Shield" className="text-accent w-4 h-4" /> Security
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="New Password" type="password" value={formData.newPassword} onChange={e => setFormData({ ...formData, newPassword: e.target.value })} placeholder="Leave blank to keep current" />
                    <Input label="Confirm New Password" type="password" value={formData.confirmPassword} onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} />
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-base-900/30 border-t border-base-700 flex justify-between items-center">
                {success ? (
                  <div className="flex items-center gap-2 text-green-400 text-sm font-bold">
                    <SafeIcon name="CheckCircle" className="w-4 h-4" /> Profile Updated Successfully
                  </div>
                ) : <div />}
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? <SafeIcon name="Loader" className="w-4 h-4 animate-spin mr-2" /> : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};